import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";

import { DeliveryTimesModal } from "./DeliveryTimesModal";

// Styled components
const StyledCard = styled(Paper)(({ theme }) => ({
  background: "transparent",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
  padding: "clamp(8px, 1.5vw, 12px) clamp(8px, 1.5vw, 12px) 4px",
  border: "1px solid lightgray",
  height: "100%",
  // maxWidth: "calc(100vw / 3)",
  minWidth: "150px",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
    fontSize: "1.2rem",
    // maxWidth: "calc(100vw / 3)",
    minWidth: "150px",
  },
  [theme.breakpoints.down("xs")]: {
    padding: "8px",
    fontSize: "1.5rem",
    // maxWidth: "calc(100vw / 3)",
    minWidth: "150px",
  },
}));

const CardHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "12px",
});

const TotalBadge = styled(Box)(({ bgcolor, textcolor }) => ({
  marginLeft: "auto",
  backgroundColor: bgcolor || "#f1f5f9",
  borderRadius: "4px",
  padding: "2px 6px",
  fontWeight: 700,
  fontSize: "11px",
  color: textcolor || "#1e293b",
  minWidth: "24px",
  textAlign: "center",
  flexShrink: 0,
}));

const Icon = styled(Box)(({ bgcolor, color }) => ({
  width: "20px",
  height: "20px",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "10px",
  backgroundColor: bgcolor,
  color: color,
}));

const InventoryItem = styled(Box)({
  display: "table",
  width: "100%",
  marginBottom: "2px",
  padding: "1px 0",
  "&:last-child": {
    marginBottom: 0,
  },
});

const InventoryRow = styled(Box)({
  display: "table-row",
});

const InventoryCell = styled(Box)({
  display: "table-cell",
  verticalAlign: "middle",
  "&.label": {
    width: "60%",
    paddingRight: "6px",
  },
  "&.value": {
    width: "40%",
    textAlign: "right",
  },
});

const ReservedSection = styled(Box)({
  marginLeft: "1px",
  marginRight: "0px",
  paddingLeft: "6px",
  marginBottom: "4px",
  borderLeft: "1px solid lightgray",
  marginTop: "2px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ReservedToggle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  padding: "1px 0",
  color: "#64748b",
  fontSize: "12px",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "#475569",
  },
  marginLeft: "4px",
});

const Triangle = styled(Box)(({ rotated }) => ({
  width: 0,
  height: 0,
  borderLeft: "4px solid transparent",
  borderRight: "4px solid transparent",
  borderTop: "6px solid currentColor",
  transition: "transform 0.2s ease",
  transform: rotated ? "rotate(180deg)" : "rotate(0deg)",
}));

const InventoryContent = styled(Box)({
  minHeight: "70px",
  display: "flex",
  flexDirection: "column",
  borderBottom: "1px solid lightgray",
});

const ExpandSection = styled(Box)({
  marginTop: "4px",
});

const ExpandToggle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  cursor: "pointer",
  padding: "1px 0",
  color: "#475569",
  fontWeight: 500,
  fontSize: "12px",
  transition: "color 0.2s ease",
  width: "100%",
  "&:hover": {
    color: "#1e293b",
  },
});

const TransitItem = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  gap: "6px",
  padding: "6px",
  background: "#f8fafc",
  borderRadius: "4px",
  marginBottom: "4px",
  "&:last-child": {
    marginBottom: 0,
  },
});

const TransitIcon = styled(Box)(({ bgcolor, color }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "3px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "10px",
  backgroundColor: bgcolor,
  color: color,
}));

const DeliveryTimesButton = styled(IconButton)({
  width: "22px",
  height: "22px",
  padding: "2px",
  borderRadius: "4px",
  backgroundColor: "#eff6ff",
  color: "#3b82f6",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#dbeafe",
    color: "#2563eb",
    transform: "scale(1.1)",
  },
});

const InventoryDetails = ({
  product,
  store,
  quantityfbaAvailable,
  quantityfbaReserved,
  quantityfbaInReceiving,
  quantityfbaInWork,
  quantity3pl,
  quantityAWD,
  quantityAWDInbound,
  quantityAWDOutbound,
  shipped_qty,
  shipped_eta,
  inWorkInShipped_qty,
  totalLTSF,
  LTSFRecommendation,
  quantityTransfer,
  quantityProcessing,
  quantityCustomerOrders,
  asinInventoryInCA,
  marketplace,
  deliveryTimesData,
  openAllInventory,
  awdInboundEta,
}) => {
  const hasFbaInboundData =
    quantityAWDOutbound > 0 || shipped_qty > 0 || inWorkInShipped_qty > 0;
  const hasAwdInboundData = quantityAWDInbound > 0;

  const [reservedExpanded, setReservedExpanded] = useState(false);
  const [fbaInboundExpanded, setFbaInboundExpanded] =
    useState(hasFbaInboundData);
  const [awdInboundExpanded, setAwdInboundExpanded] =
    useState(hasAwdInboundData);
  const toggleReserved = () => setReservedExpanded(!reservedExpanded);
  const toggleFbaInbound = () => setFbaInboundExpanded(!fbaInboundExpanded);
  const toggleAwdInbound = () => setAwdInboundExpanded(!awdInboundExpanded);
  const [deliveryTimesOpen, setDeliveryTimesOpen] = useState(false);

  const childAsin = product?.child_asin || product?.parent_asin;

  const deliveryStatus = useMemo(() => {
    if (!deliveryTimesData || !deliveryTimesData.length || !childAsin)
      return { show: false };

    const asinTimes = deliveryTimesData.filter(
      (item) => item.child_asin === childAsin
    );
    if (!asinTimes.length) return { show: false };

    const latestDate = asinTimes.reduce((max, item) => {
      if (!item.last_updated_date) return max;
      return !max || item.last_updated_date > max
        ? item.last_updated_date
        : max;
    }, null);

    if (!latestDate) return { show: false };

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const latestDateObj = new Date(latestDate);
    if (latestDateObj < oneWeekAgo) return { show: false };

    const latestItems = asinTimes.filter(
      (item) => item.last_updated_date === latestDate
    );
    const hasSlow = latestItems.some((item) => {
      const sampleDate = item.sample_date || item.last_updated_date;
      if (!sampleDate || !item.prime_end_date) return false;
      const days = Math.round(
        (new Date(item.prime_end_date) - new Date(sampleDate)) / 86400000
      );
      return days >= 5;
    });

    return { show: true, isSlow: hasSlow };
  }, [deliveryTimesData, childAsin]);

  useEffect(() => {
    if (openAllInventory !== undefined) {
      setReservedExpanded(openAllInventory);
      setFbaInboundExpanded(openAllInventory);
      setAwdInboundExpanded(openAllInventory);
    }
  }, [openAllInventory]);

  useEffect(() => {
    setFbaInboundExpanded(hasFbaInboundData);
  }, [hasFbaInboundData]);

  useEffect(() => {
    setAwdInboundExpanded(hasAwdInboundData);
  }, [hasAwdInboundData]);

  const isNoInventoryInCanada =
    marketplace === "ca" && asinInventoryInCA && asinInventoryInCA.val === 0;

  return (
    <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Canada-specific inventory display */}
      {marketplace === "ca" && asinInventoryInCA && (
        <Box sx={{ marginBottom: "16px" }}>
          <StyledCard
            sx={{
              backgroundColor: "#f0f9ff",
              borderColor: "#bae6fd",
              padding: "10px",
            }}
          >
            <Typography sx={{ color: "#0369a1", fontWeight: 500 }}>
              🇨🇦 Inventory stored in Canada:{" "}
              <strong>{asinInventoryInCA.val || 0}</strong>
            </Typography>
          </StyledCard>
        </Box>
      )}
      <Box
        sx={{
          opacity: isNoInventoryInCanada ? 0.25 : 1,
          pointerEvents: isNoInventoryInCanada ? "none" : "auto",
          transition: "opacity 0.3s ease",
        }}
      >
        <Grid container spacing={{ xs: 0.5, sm: 1.5, md: 2.5 }}>
          {/* FBA Card */}
          <Grid item xs={4} sx={{ paddingLeft: "0px !important" }}>
            <StyledCard>
              <CardHeader>
                <Icon bgcolor="#dbeafe" color="#2563eb">
                  🏠
                </Icon>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "12px",
                  }}
                >
                  FBA
                </Typography>
                {deliveryStatus.show && marketplace !== "ca" && (
                  <Tooltip
                    title={
                      deliveryStatus.isSlow
                        ? "Slow delivery times detected"
                        : "View delivery times across US"
                    }
                    arrow
                  >
                    <DeliveryTimesButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeliveryTimesOpen(true);
                      }}
                      size="small"
                      sx={
                        deliveryStatus.isSlow
                          ? {
                              backgroundColor: "#fef2f2",
                              color: "#dc2626",
                              "&:hover": {
                                backgroundColor: "#fee2e2",
                                color: "#b91c1c",
                              },
                            }
                          : {}
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        <LocalShippingIcon sx={{ fontSize: "14px" }} />
                        <CalendarTodayIcon
                          sx={{
                            fontSize: "7px",
                            position: "absolute",
                            bottom: -1,
                            right: -3,
                            backgroundColor: deliveryStatus.isSlow
                              ? "#fef2f2"
                              : "#eff6ff",
                            borderRadius: "2px",
                          }}
                        />
                      </Box>
                    </DeliveryTimesButton>
                  </Tooltip>
                )}
                <TotalBadge bgcolor="#dbeafe" textcolor="#2563eb">
                  {(quantityfbaAvailable || 0) +
                    (quantityfbaReserved || 0) +
                    (quantityfbaInReceiving || 0)}
                </TotalBadge>
              </CardHeader>

              <InventoryContent>
                <InventoryItem>
                  <InventoryRow>
                    <InventoryCell className="label">
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#475569",
                          fontSize: "12px",
                        }}
                      >
                        Available:
                      </Typography>
                    </InventoryCell>
                    <InventoryCell className="value">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {quantityfbaAvailable || 0}
                      </Typography>
                    </InventoryCell>
                  </InventoryRow>
                </InventoryItem>
                <InventoryItem>
                  <InventoryRow>
                    <InventoryCell className="label">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#475569",
                            fontSize: "12px",
                          }}
                        >
                          Reserved:
                        </Typography>
                        <ReservedToggle onClick={toggleReserved}>
                          <Triangle rotated={reservedExpanded} />
                        </ReservedToggle>
                      </Box>
                    </InventoryCell>
                    <InventoryCell className="value">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {quantityfbaReserved || 0}
                      </Typography>
                    </InventoryCell>
                  </InventoryRow>
                </InventoryItem>
                {reservedExpanded && (
                  <ReservedSection>
                    <Box
                      sx={{
                        display: "table",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "table-row" }}>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "60%",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          FC Transfer:
                        </Box>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "40%",
                            textAlign: "right",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {quantityTransfer || 0}
                        </Box>
                      </Box>
                      <Box sx={{ display: "table-row" }}>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "60%",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          FC Processing:
                        </Box>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "40%",
                            textAlign: "right",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {quantityProcessing || 0}
                        </Box>
                      </Box>
                      <Box sx={{ display: "table-row" }}>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "60%",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          Customer Orders:
                        </Box>
                        <Box
                          sx={{
                            display: "table-cell",
                            width: "40%",
                            textAlign: "right",
                            padding: "2px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {quantityCustomerOrders || 0}
                        </Box>
                      </Box>
                    </Box>
                  </ReservedSection>
                )}
                {quantityfbaInReceiving > 0 && (
                  <InventoryItem>
                    <InventoryRow>
                      <InventoryCell className="label">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#475569",
                            fontSize: "12px",
                          }}
                        >
                          In Receiving:
                        </Typography>
                      </InventoryCell>
                      <InventoryCell className="value">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#1e293b",
                            fontSize: "12px",
                          }}
                        >
                          {quantityfbaInReceiving || 0}
                        </Typography>
                      </InventoryCell>
                    </InventoryRow>
                  </InventoryItem>
                )}
              </InventoryContent>

              <ExpandSection>
                <ExpandToggle onClick={toggleFbaInbound}>
                  <Triangle rotated={fbaInboundExpanded} />
                  <Typography sx={{ fontSize: "12px" }}>Inbound</Typography>
                </ExpandToggle>
                {fbaInboundExpanded && (
                  <Box sx={{ marginTop: "5px" }}>
                    {quantityAWDOutbound > 0 && (
                      <TransitItem>
                        <Tooltip title="Incoming from AWD/3PL" arrow>
                          <TransitIcon bgcolor="#fef3c7" color="#16a34a">
                            📦
                          </TransitIcon>
                        </Tooltip>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#475569",
                              fontSize: "12px",
                            }}
                          >
                            {quantityAWDOutbound}
                          </Typography>
                        </Box>
                      </TransitItem>
                    )}

                    {shipped_qty > 0 && (
                      <TransitItem>
                        <Tooltip title="Incoming from factory" arrow>
                          <Icon bgcolor="#dcfce7" color="#16a34a">
                            🏭
                          </Icon>
                        </Tooltip>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#475569",
                              fontSize: "12px",
                            }}
                          >
                            {shipped_qty}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#64748b",
                            }}
                          >
                            ETA:{" "}
                            {shipped_eta && shipped_eta.length > 0
                              ? shipped_eta.length > 1
                                ? "Multiple dates"
                                : shipped_eta[0]
                                    .split("\n")
                                    .map((line, i, arr) => (
                                      <React.Fragment key={i}>
                                        {line}
                                        {i < arr.length - 1 && (
                                          <>
                                            <br />
                                            <span
                                              style={{ marginLeft: "2.4em" }}
                                            />
                                          </>
                                        )}
                                      </React.Fragment>
                                    ))
                              : "TBD"}
                          </Typography>
                        </Box>
                      </TransitItem>
                    )}
                    {inWorkInShipped_qty > 0 && !shipped_qty && (
                      <TransitItem>
                        <Tooltip
                          title="Incoming from factory (amazon data)"
                          arrow
                        >
                          <Icon bgcolor="#dcfce7" color="#16a34a">
                            🏭
                          </Icon>
                        </Tooltip>
                        <Tooltip
                          title="Incoming from factory (amazon data)"
                          arrow
                        >
                          <Icon
                            bgcolor="#dcfce7"
                            color="#16a34a"
                            style={{ marginLeft: "-10px" }}
                          >
                            +🚚
                          </Icon>
                        </Tooltip>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#475569",
                              fontSize: "12px",
                            }}
                          >
                            {inWorkInShipped_qty}
                          </Typography>
                        </Box>
                      </TransitItem>
                    )}
                    {!quantityAWDOutbound &&
                      !shipped_qty &&
                      !inWorkInShipped_qty && (
                        <Box
                          sx={{
                            padding: "6px",
                            textAlign: "center",
                            color: "#64748b",
                            fontStyle: "italic",
                            fontSize: "clamp(11px, 0.9vw, 13px)",
                          }}
                        >
                          No inbound items
                        </Box>
                      )}
                  </Box>
                )}
              </ExpandSection>
            </StyledCard>
          </Grid>

          {/* AWD & 3PL Card */}
          <Grid item xs={4}>
            <StyledCard>
              <CardHeader>
                <Icon bgcolor="#fef3c7" color="#d97706">
                  📦
                </Icon>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "clamp(10px, 0.8vw, 12px)",
                    whiteSpace: "nowrap",
                  }}
                >
                  AWD & 3PL
                </Typography>
                <TotalBadge bgcolor="#fef3c7" textcolor="#d97706">
                  {(quantityAWD || 0) + (quantity3pl || 0)}
                </TotalBadge>
              </CardHeader>

              <InventoryContent>
                <InventoryItem>
                  <InventoryRow>
                    <InventoryCell className="label">
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#475569",
                          fontSize: "12px",
                        }}
                      >
                        AWD:
                      </Typography>
                    </InventoryCell>
                    <InventoryCell className="value">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {quantityAWD || 0}
                      </Typography>
                    </InventoryCell>
                  </InventoryRow>
                </InventoryItem>

                <InventoryItem>
                  <InventoryRow>
                    <InventoryCell className="label">
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#475569",
                          fontSize: "12px",
                        }}
                      >
                        3PL:
                      </Typography>
                    </InventoryCell>
                    <InventoryCell className="value">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {quantity3pl || 0}
                      </Typography>
                    </InventoryCell>
                  </InventoryRow>
                </InventoryItem>
              </InventoryContent>

              <ExpandSection>
                <ExpandToggle onClick={toggleAwdInbound}>
                  <Triangle rotated={awdInboundExpanded} />
                  <Typography sx={{ fontSize: "12px" }}>Inbound</Typography>
                </ExpandToggle>
                {awdInboundExpanded && (
                  <Box sx={{ marginTop: "5px" }}>
                    {quantityAWDInbound > 0 && (
                      <TransitItem>
                        <Tooltip title="Incoming from factory" arrow>
                          <TransitIcon bgcolor="#dcfce7" color="#16a34a">
                            🏭
                          </TransitIcon>
                        </Tooltip>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#1e293b",
                              marginBottom: "2px",
                            }}
                          >
                            {quantityAWDInbound}
                          </Typography>
                          {awdInboundEta && awdInboundEta.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: "10px",
                                color: "#64748b",
                              }}
                            >
                              ETA:{" "}
                              {awdInboundEta.length > 1
                                ? "Multiple dates"
                                : awdInboundEta[0]}
                            </Typography>
                          )}
                        </Box>
                      </TransitItem>
                    )}
                    {!quantityAWDInbound && (
                      <Box
                        sx={{
                          padding: "6px",
                          textAlign: "center",
                          color: "#64748b",
                          fontStyle: "italic",
                          fontSize: "clamp(11px, 0.9vw, 13px)",
                        }}
                      >
                        No inbound items
                      </Box>
                    )}
                  </Box>
                )}
              </ExpandSection>
            </StyledCard>
          </Grid>

          {/* Manufacturing Card */}
          <Grid item xs={4}>
            <StyledCard>
              <CardHeader>
                <Icon bgcolor="#dcfce7" color="#16a34a">
                  🏭
                </Icon>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "clamp(10px, 0.8vw, 12px)",
                  }}
                >
                  Manufacturing
                </Typography>
              </CardHeader>

              <InventoryContent>
                <InventoryItem>
                  <InventoryRow>
                    <InventoryCell className="label">
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: "#475569",
                          fontSize: "12px",
                        }}
                      >
                        In Work:
                      </Typography>
                    </InventoryCell>
                    <InventoryCell className="value">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "12px",
                        }}
                      >
                        {quantityfbaInWork || 0}
                      </Typography>
                    </InventoryCell>
                  </InventoryRow>
                </InventoryItem>
              </InventoryContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* LTSF */}
        {totalLTSF && totalLTSF.fees > 0 && (
          <Box sx={{ marginTop: "24px" }}>
            <Tooltip
              title={
                product?.children ? (
                  "Hover over child ASINs to see recommendations"
                ) : LTSFRecommendation?.neededVelocityIncrease <= 0 ? (
                  "Your current sales velocity is on track to avoid Long-Term Storage Fees for this ASIN. No action is needed at this time"
                ) : (
                  <>
                    By selling {LTSFRecommendation?.neededUnitSales} more units
                    this month you can save $
                    {LTSFRecommendation?.possibleSavings} of long term storage
                    fees next month. You need to increase your velocity by{" "}
                    {LTSFRecommendation?.neededVelocityIncrease} units per day
                  </>
                )
              }
            >
              <StyledCard
                sx={{
                  backgroundColor: "#fef2f2",
                  borderColor: "#fecaca",
                  "&:hover": {
                    backgroundColor: "#fef2f2",
                  },
                  padding: "5px",
                }}
              >
                <Typography sx={{ color: "#dc2626", fontWeight: 500 }}>
                  ⚠️ {totalLTSF.units} units will be charged ${totalLTSF.fees}{" "}
                  in LTSF at the end of the month
                </Typography>
              </StyledCard>
            </Tooltip>
          </Box>
        )}
      </Box>

      <DeliveryTimesModal
        open={deliveryTimesOpen}
        onClose={() => setDeliveryTimesOpen(false)}
        store={store}
        childAsin={childAsin}
        deliveryTimesData={deliveryTimesData}
      />
    </Box>
  );
};

export { InventoryDetails };
